import React, { useMemo } from "react";
import type { ReactElement, ComponentProps } from "react";

import { HStack, Text, ChevronDownIcon, Center, Spinner } from "native-base";
import type { Icon } from "native-base";

import {
  DraftIcon,
  SendIcon,
  TickIcon,
  PendingIcon,
  BlockIcon,
  DraftFilledIcon,
  DoubleTickIcon,
  DraftUpdatedIcon,
  DraftFilledUpdatedIcon,
  CashIcon,
  ClockCheckIcon,
  WaitingListIcon,
  NewEnrollmentIcon,
  ArchivedIcon,
} from "pianofunclub-shared/components/Other/Icons";
import { titleCaseConverter } from "pianofunclub-shared/utils/converters";

type HStackProps = ComponentProps<typeof HStack>;
type TextProps = ComponentProps<typeof Text>;
type IconProps = ComponentProps<typeof Icon>;

interface Props extends HStackProps {
  iconProps?: IconProps;
  isLoading?: boolean;
  isTransparent?: boolean;
  isUpdatedDraft?: boolean | null;
  label?: string;
  showEditArrow?: boolean;
  status?: string;
  textProps?: TextProps;
}

const ItemStatus = (props: Props): ReactElement | null => {
  const {
    iconProps,
    isLoading,
    isTransparent,
    isUpdatedDraft,
    label,
    showEditArrow,
    status,
    textProps,
  } = props;

  const { color, endColor, icon, startColor, textColor } = useMemo(() => {
    if (!isTransparent) {
      switch (status) {
        case "DRAFT":
          return {
            endColor: "surface.600",
            icon: !isUpdatedDraft ? (
              <DraftIcon color="surface.50" size="6" {...iconProps} />
            ) : (
              <DraftUpdatedIcon color="surface.50" size="6" {...iconProps} />
            ),
            startColor: "surface.400",
            textColor: "surface.50",
          };
        case "SENT":
        case "OFFERED":
          return {
            endColor: "primary.500",
            icon: <SendIcon color="surface.50" size="4" {...iconProps} />,
            startColor: "primary.300",
            textColor: "surface.50",
          };
        case "OFFER_EXPIRED":
          return {
            endColor: "gray.500",
            icon: <SendIcon color="surface.50" size="4" {...iconProps} />,
            startColor: "gray.300",
            textColor: "surface.50",
          };
        case "ACTIVE":
        case "PAID":
        case "ENROLED":
          return {
            endColor: "primary.600",
            icon: <TickIcon color="surface.50" size="6" {...iconProps} />,
            startColor: "primary.400",
            textColor: "surface.50",
          };
        case "PENDING":
        case "NOT_VIEWED":
          return {
            endColor: "orange.500",
            icon: <PendingIcon color="surface.50" size="6" {...iconProps} />,
            startColor: "orange.300",
            textColor: "surface.50",
          };
        case "WAITING_LIST":
          return {
            endColor: "surface.700",
            icon: <WaitingListIcon color="surface.50" size="6" {...iconProps} />,
            startColor: "surface.500",
            textColor: "surface.50",
          };
        case "NEW_ENROLLMENT":
          return {
            endColor: "primary.200",
            icon: <NewEnrollmentIcon color="surface.50" size="6" {...iconProps} />,
            startColor: "primary.100",
            textColor: "surface.50",
          };
        case "OVERPAID":
          return {
            endColor: "primary.800",
            icon: <DoubleTickIcon color="surface.50" size="6" {...iconProps} />,
            startColor: "primary.600",
            textColor: "surface.50",
          };
        case "REFUND":
          return {
            endColor: "secondary.700",
            icon: <CashIcon color="surface.50" size="6" {...iconProps} />,
            startColor: "secondary.500",
            textColor: "surface.50",
          };
        case "READY_TO_PAY":
          return {
            endColor: "yellow.600",
            icon: <ClockCheckIcon color="surface.50" size="6" {...iconProps} />,
            startColor: "yellow.400",
            textColor: "surface.50",
          };
        case "ARCHIVED":
          return {
            endColor: "surface.800",
            icon: <ArchivedIcon color="surface.50" size="5" {...iconProps} />,
            startColor: "surface.600",
            textColor: "surface.50",
          };
        default:
          return {
            endColor: "surface.800",
            icon: <BlockIcon color="surface.50" size="6" {...iconProps} />,
            startColor: "surface.600",
            textColor: "surface.50",
          };
      }
    } else {
      switch (status) {
        case "DRAFT":
          return {
            color: "transparent",
            icon: !isUpdatedDraft ? (
              <DraftFilledIcon color="surface.700" size="6" {...iconProps} />
            ) : (
              <DraftFilledUpdatedIcon color="surface.700" size="6" {...iconProps} />
            ),
            textColor: "surface.700",
          };
        case "SENT":
        case "OFFERED":
          return {
            color: "transparent",
            icon: <SendIcon color="primary.500" size="4" {...iconProps} />,
            textColor: "primary.500",
          };
        case "OFFER_EXPIRED":
          return {
            color: "transparent",
            icon: <SendIcon color="gray.500" size="4" {...iconProps} />,
            textColor: "gray.500",
          };
        case "ACTIVE":
        case "PAID":
        case "ENROLED":
          return {
            color: "transparent",
            icon: <TickIcon color="primary.600" size="6" {...iconProps} />,
            textColor: "primary.600",
          };
        case "PENDING":
        case "NOT_VIEWED":
          return {
            color: "transparent",
            icon: <PendingIcon color="orange.500" size="6" {...iconProps} />,
            textColor: "orange.500",
          };
        case "WAITING_LIST":
          return {
            color: "transparent",
            icon: <WaitingListIcon color="surface.800" size="6" {...iconProps} />,
            textColor: "surface.800",
          };
        case "NEW_ENROLLMENT":
          return {
            color: "transparent",
            icon: <NewEnrollmentIcon color="primary.400" size="6" {...iconProps} />,
            textColor: "primary.400",
          };
        case "OVERPAID":
          return {
            color: "transparent",
            icon: <DoubleTickIcon color="primary.800" size="6" {...iconProps} />,
            textColor: "primary.800",
          };
        case "REFUND":
          return {
            color: "transparent",
            icon: <CashIcon color="secondary.600" size="6" {...iconProps} />,
            textColor: "secondary.600",
          };
        case "READY_TO_PAY":
          return {
            color: "transparent",
            icon: <ClockCheckIcon color="yellow.600" size="6" {...iconProps} />,
            textColor: "yellow.600",
          };
        case "ARCHIVED":
          return {
            color: "transparent",
            icon: <ArchivedIcon color="surface.800" size="6" {...iconProps} />,
            textColor: "surface.800",
          };
        default:
          return {
            color: "transparent",
            icon: <BlockIcon color="surface.800" size="6" {...iconProps} />,
            textColor: "surface.800",
          };
      }
    }
  }, [iconProps, isTransparent, isUpdatedDraft, status]);

  if (!status) {
    return null;
  }

  return (
    <HStack
      bg={
        startColor && endColor
          ? {
              linearGradient: {
                colors: [startColor, endColor],
                end: [0.75, 1],
                start: [0.25, 0],
              },
            }
          : color
      }
      borderRadius="2xl"
      pl="3"
      pr="4"
      py="2"
      shadow={!isTransparent && showEditArrow ? "1" : undefined}
      space="2"
      {...props}
    >
      <Center size="6">
        {!isLoading ? icon : <Spinner color={isTransparent ? textColor : "surface.50"} />}
      </Center>
      <Text color={textColor} fontSize="md" {...textProps}>
        {label ?? titleCaseConverter(status)}
      </Text>
      {showEditArrow ? (
        <ChevronDownIcon color={isTransparent ? "surface.500" : "surface.100"} />
      ) : null}
    </HStack>
  );
};

export default ItemStatus;
